import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'employee' | 'viewer';
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Role-based permissions mapping
  const rolePermissions: Record<string, string[]> = {
    admin: ['*'], // Admin has all permissions
    manager: [
      'dashboard.view',
      'orders.*',
      'products.*',
      'vendors.*',
      'accounting.*',
      'users.view',
      'activity.view'
    ],
    employee: [
      'dashboard.view',
      'orders.view',
      'orders.update',
      'products.view',
      'vendors.view'
    ],
    viewer: [
      'dashboard.view',
      'orders.view',
      'products.view',
      'vendors.view'
    ]
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    const userPermissions = rolePermissions[user.role] || [];
    
    // Check for wildcard permission
    if (userPermissions.includes('*')) return true;
    
    // Check exact permission
    if (userPermissions.includes(permission)) return true;
    
    // Check wildcard patterns (e.g., orders.* matches orders.view)
    return userPermissions.some(perm => {
      if (perm.endsWith('.*')) {
        const prefix = perm.replace('.*', '');
        return permission.startsWith(prefix);
      }
      return false;
    });
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await api.login(email, password) as unknown as {
        status: string;
        token: string;
        user: User;
      };

      if (response.status === 'success' && response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    
    // Call logout API endpoint
    api.logout?.().catch(console.error);
  };

  // Check for existing session on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          // Verify token is still valid by calling /me endpoint
          try {
            const response = await api.getCurrentUser?.() as unknown as {
              status: string;
              user: User;
            };
            
            if (response.status === 'success') {
              setUser(response.user);
            } else {
              // Token is invalid, clear storage
              logout();
            }
          } catch (error) {
            // If API call fails, use cached user data but user might need to re-login soon
            setUser(parsedUser);
          }
        } catch (error) {
          console.error('Failed to parse user data:', error);
          logout();
        }
      }
      
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 